/**
 * REV-810: the allowed-origins list on a tracked place, as the Settings form
 * edits it.
 *
 * A tracked place accepts evidence about its own origin plus the origins on
 * this list — an apex and a www host, a staging host, a preview host the
 * customer owns. The form edits the list as text, one origin per line, because
 * that is the shape people paste it in.
 *
 * The API is the authority: `contexts.allowed_origins` normalizes every entry
 * and refuses anything that is not an origin (revenant-api
 * `src/models/Context.js`, `src/utils/scopeBounds.js`). What is here exists so
 * the form can say which line is wrong before the request goes, and so a
 * pasted `https://www.example.com/` is sent in the form the API stores.
 */

/**
 * The same host test as revenant-api `hostOfIdentifier`, and deliberately no
 * stricter: a stored origin this form refused would block every later save.
 */
const HOST = /^[a-z0-9._-]+(?::\d+)?$/;
const BRACKETED_IPV6 = /^\[[0-9a-f:.]+\](?::\d+)?$/;

/**
 * One line of the editor as the API would store it: scheme, userinfo, path and
 * a default port removed, host lowercased. '' when the line names no origin.
 */
export function normalizeOrigin(value: string): string {
  const trimmed = (value ?? '').trim();
  if (!trimmed) {
    return '';
  }
  let rest = trimmed.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '');
  // A backslash ends the authority too, as it does for `new URL()`: the API
  // reads `https://evil.com\@example.com/` as evil.com, so this must as well.
  rest = rest.split(/[/?#\\]/)[0] ?? '';
  const at = rest.lastIndexOf('@');
  if (at !== -1) {
    rest = rest.slice(at + 1);
  }
  const host = rest.toLowerCase().replace(/:(?:80|443)$/, '');
  // A leading dot is a dot-directory (`.storybook/preview.tsx`), never a host.
  if (!host || host.startsWith('.')) {
    return '';
  }
  if (BRACKETED_IPV6.test(host)) {
    return host;
  }
  if (!HOST.test(host)) {
    return '';
  }
  // A single label with no port is a word, not an address — except localhost,
  // which really is one.
  if (!host.includes('.') && !/:\d+$/.test(host) && host !== 'localhost') {
    return '';
  }
  return host;
}

/** The lines of the editor, blank ones dropped. */
export function originLines(text: string): string[] {
  return (text ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/** The lines that name no origin, so the form can point at them. */
export function invalidOriginLines(text: string): string[] {
  return originLines(text).filter((line) => !normalizeOrigin(line));
}

/**
 * The list to send: normalized, de-duplicated, in the order typed. null when
 * the editor is empty, which clears the list.
 */
export function originsToSend(text: string): string[] | null {
  const origins: string[] = [];
  for (const line of originLines(text)) {
    const origin = normalizeOrigin(line);
    if (origin && !origins.includes(origin)) {
      origins.push(origin);
    }
  }
  return origins.length > 0 ? origins : null;
}

/** The stored list as the editor shows it. */
export function originsToText(origins: string[] | null | undefined): string {
  return (origins ?? []).join('\n');
}
